import React, { useMemo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import FuelAssetItem from './FuelAssetItem';
import { Box, LinearProgress, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import SortBy from './SortBy';
import { type fuelMeasureType } from 'views/FuelHistoricalAnalysis/types/fuelAnalytics.types';
import ProductionSelector from 'views/Production/components/Selector/Selector';
import { useTranslation } from 'react-i18next';
import { getFuelUsageMeasureLabel } from '../utils';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface Props {
  assetType: string;
  title: string;
  hiddenDropDownItems?: fuelMeasureType[];
  aggregation: string;
}

const FuelAssetAnalyticsSection = ({
  assetType,
  title,
  hiddenDropDownItems,
  aggregation,
}: Props) => {
  const { t } = useTranslation();
  const dropdownItems = useMemo(() => {
    const items = [
      'Consumption',
      'Co2 Emission',
      'Num of Idle Events',
      'Total Duration of Idle Events',
      'Loaded Fuel Consumption',
      'Unloaded Fuel Consumption',
      'Avg Fuel Rate',
      `Fuel Consumed Per Ton`,
      'Overspeeding',
      'Underspeeding',
      'Gears',
      'Short Idling',
      'Long Idling',
      'Queuing',
    ];
    if (assetType === 'loader') {
      items.splice(items.indexOf('Fuel Consumed Per Ton'), 1);
    }

    return items.filter(
      (item) =>
        !hiddenDropDownItems?.includes(
          item.toLowerCase()?.split(' ')?.join('_') as fuelMeasureType
        )
    );
  }, [hiddenDropDownItems]);
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const {
    loadingStates: { isLoadingFuelTreemapData },
    fleetDeviceDetailsPerDayBasis,
  } = useAppSelector((state) => state.fuelAnalyticsReducer);

  const [selectedSource, setSelectedSource] = useState<string[]>([
    'Consumption',
  ]);

  const [sortBy, setSortBy] = useState<string>('most fuel consumed');
  const sortOptionLabels: Record<string, string> = {
    'most fuel consumed': t('fuel_usage_sort_most_fuel_consumed'),
    'least fuel consumed': t('fuel_usage_sort_least_fuel_consumed'),
  };
  const getSourceLabel = (source: string) =>
    getFuelUsageMeasureLabel(t, source);
  const handleSource = (value: any) => {
    if (selectedSource.length > 2 || value > 2 || value <= 0) {
      return;
    }
    setSelectedSource(value);
  };

  const selectedAssetTypeDataPerDayBasis = useMemo(() => {
    const filteredData = fleetDeviceDetailsPerDayBasis.filter(
      (item) => item.asset_type === assetType
    );
    const sortedData = [...filteredData].sort((a, b) => {
      if (sortBy === 'most fuel consumed') {
        return b.total_fuel_consumption - a.total_fuel_consumption;
      }
      return a.total_fuel_consumption - b.total_fuel_consumption;
    });
    return sortedData;
  }, [assetType, fleetDeviceDetailsPerDayBasis, sortBy]);

  return (
    <div
      style={{
        padding: '16px',
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.boxBackground,
        borderRadius: '5px',
        border: `1px solid ${palette.cardBorderColor}`,
        width: '100%',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          marginBottom: '15px',
        }}
      >
        <Box>
          <Typography
            sx={{
              fontSize: '13px',
              fontWeight: '600',
              paddingTop: '5px',
              color: palette.textPrimary,
            }}
          >
            {title}
          </Typography>
        </Box>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <ProductionSelector
            options={dropdownItems}
            selected={selectedSource}
            setSelected={handleSource}
            multiple
            minWidth="200px"
            maxWidth="200px"
            getOptionLabel={getSourceLabel}
          />
          <SortBy
            setSelected={setSortBy}
            options={['most fuel consumed', 'least fuel consumed']}
            selected={sortBy}
            getOptionLabel={(option) => sortOptionLabels[option] ?? option}
          />
        </Box>
      </Box>
      {isLoadingFuelTreemapData ? (
        <LinearProgress
          color="secondary"
          sx={{
            marginTop: '65px',
          }}
        />
      ) : (
        selectedAssetTypeDataPerDayBasis.map((item, index) => {
          return (
            <FuelAssetItem
              key={index}
              data={item}
              selectedSource={selectedSource}
              aggregation={aggregation}
              getSourceLabel={getSourceLabel}
            />
          );
        })
      )}
    </div>
  );
};

export default FuelAssetAnalyticsSection;
